import React, { useMemo, useState } from "react";
import {
  ArrowUpDown,
  CalendarDays,
  Check,
  ChevronDown,
  RotateCcw,
  Search,
  Tags,
  X,
} from "lucide-react";
import {
  QUICK_FILTERS,
  SORT_OPTIONS,
  TAG_OPTIONS,
} from "../constant/FilterData";

function ContactsFilters({
  filters = {},
  totalContacts = 0,
  loading = false,
  onSearchChange,
  onTagChange,
  onDateChange,
  onSortChange,
  onQuickFilter,
  onReset,
}) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dateMode, setDateMode] = useState("range");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const searchValue = filters?.search || "";
  const selectedTag = filters?.tag || "all";
  const selectedSort = filters?.sortBy || "newest";
  const selectedQuickFilter = filters?.quickFilter || "all";

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchValue.trim()) count += 1;
    if (selectedTag !== "all") count += 1;
    if (selectedSort !== "newest") count += 1;
    if (selectedQuickFilter !== "all") count += 1;
    if (dateFrom || dateTo) count += 1;
    return count;
  }, [searchValue, selectedTag, selectedSort, selectedQuickFilter, dateFrom, dateTo]);

  const selectedTagLabel = TAG_OPTIONS.find(
    (item) => item.value === selectedTag,
  )?.label || "All tags";
  const selectedSortLabel = SORT_OPTIONS.find(
    (item) => item.value === selectedSort,
  )?.label || "Newest first";

  const toggleDropdown = (name) => {
    setOpenDropdown((current) => current === name ? null : name);
  };

  const handleDateFrom = (event) => {
    const value = event.target.value;
    setDateFrom(value);

    if (dateMode === "single") {
      setDateTo(value);
      onDateChange?.({ from: value, to: value });
      return;
    }

    onDateChange?.({ from: value, to: dateTo });
  };

  const handleDateTo = (event) => {
    const value = event.target.value;
    setDateTo(value);
    onDateChange?.({ from: dateFrom, to: value });
  };

  const handleToday = () => {
    const today = new Date().toISOString().slice(0, 10);
    setDateFrom(today);
    setDateTo(today);
    onDateChange?.({ from: today, to: today });
  };

  const handleClearDate = () => {
    setDateFrom("");
    setDateTo("");
    onDateChange?.(null);
    setOpenDropdown(null);
  };

  const handleReset = () => {
    setDateFrom("");
    setDateTo("");
    setDateMode("range");
    setOpenDropdown(null);
    onReset?.();
  };

  const controlClass =
    "flex h-11 items-center gap-2.5 rounded-xl border border-[#e1eaef] bg-[#ffffff] px-3 text-left text-[11px] font-semibold text-[#34506a] outline-none transition-all hover:border-[#c7dce5] hover:bg-white focus:border-[#147db5] focus:ring-2 focus:ring-[#147db5]/10 disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#344b43] dark:bg-[#182521] dark:text-[#c7d8d1]";

  const menuClass =
    "absolute top-[50px] z-[90] overflow-hidden rounded-2xl border border-[#e1eaef] bg-[#ffffff] p-1.5 shadow-[0_20px_48px_rgba(34,56,47,0.16)] dark:border-[#344c43] dark:bg-[#182621]";

  return (
    <section className="relative z-30 border-b border-[#e1eaef] bg-[#ffffff] px-3 py-4 sm:px-6 sm:py-5 dark:border-[#2b4039] dark:bg-[#121d19]">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="group relative min-w-0 flex-1">
          <Search
            size={18}
            strokeWidth={1.8}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#8a9ba8] transition-colors group-focus-within:text-[#147db5]"
          />
          <input
            type="search"
            value={searchValue}
            onChange={(event) => onSearchChange?.(event.target.value)}
            placeholder="Search people, phone numbers or details"
            className="h-12 w-full rounded-2xl border border-[#e1eaef] bg-[#f6f9fb] pl-12 pr-11 text-[12px] font-semibold text-[#102f45] outline-none transition-all placeholder:font-medium placeholder:text-[#8a9ba8] hover:border-[#c7dce5] focus:border-[#147db5] focus:bg-white focus:ring-4 focus:ring-[#147db5]/[0.07] disabled:cursor-not-allowed disabled:opacity-50 dark:border-[#344b43] dark:bg-[#101915] dark:text-[#e2ece7] dark:placeholder:text-[#72877f]"
          />
          {searchValue ? (
            <button
              type="button"
              onClick={() => onSearchChange?.("")}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-[#8a9ba8] transition-colors hover:bg-[#eaf4fa] hover:text-[#147db5]"
            >
              <X size={14} strokeWidth={2} />
            </button>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-[minmax(130px,0.8fr)_minmax(150px,1fr)_minmax(140px,0.9fr)_auto] lg:flex lg:shrink-0">
          <div className="relative min-w-0">
            <button
              type="button"
              disabled={loading}
              onClick={() => toggleDropdown("tags")}
              className={`${controlClass} w-full lg:w-[145px]`}
            >
              <Tags size={15} className="shrink-0 text-[#b77732]" />
              <span className="min-w-0 flex-1 truncate">{selectedTagLabel}</span>
              <ChevronDown
                size={14}
                className={`shrink-0 text-[#849ba7] transition-transform ${openDropdown === "tags" ? "rotate-180" : ""}`}
              />
            </button>

            {openDropdown === "tags" ? (
              <div className={`${menuClass} left-0 w-[210px]`}>
                {TAG_OPTIONS.map((option) => {
                  const active = selectedTag === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        onTagChange?.(option.value);
                        setOpenDropdown(null);
                      }}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[11px] font-semibold transition-colors ${active ? "bg-[#eaf4fa] text-[#0f6392] dark:bg-[#1c372d] dark:text-[#79c8a9]" : "text-[#34506a] hover:bg-[#f6f9fb] dark:text-[#bacbc4] dark:hover:bg-[#22342e]"}`}
                    >
                      <span className={`h-2 w-2 rounded-full ${active ? "bg-[#147db5]" : "bg-[#c7dce5] dark:bg-[#465f56]"}`} />
                      <span className="flex-1">{option.label}</span>
                      {active ? <Check size={13} strokeWidth={2.5} /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="relative min-w-0">
            <button
              type="button"
              disabled={loading}
              onClick={() => toggleDropdown("date")}
              className={`${controlClass} w-full lg:w-[175px]`}
            >
              <CalendarDays size={15} className="shrink-0 text-[#20a777]" />
              <span className="min-w-0 flex-1 truncate">
                {dateFrom && dateTo
                  ? `${dateFrom} – ${dateTo}`
                  : dateFrom
                    ? `From ${dateFrom}`
                    : "Created date"}
              </span>
              <ChevronDown
                size={14}
                className={`shrink-0 text-[#849ba7] transition-transform ${openDropdown === "date" ? "rotate-180" : ""}`}
              />
            </button>

            {openDropdown === "date" ? (
              <div className={`${menuClass} right-0 w-[min(320px,calc(100vw-40px))] p-4 lg:left-0 lg:right-auto`}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[12px] font-bold text-[#173044] dark:text-[#e1edf2]">Created date</p>
                    <p className="mt-1 text-[10px] text-[#8097a4] dark:text-[#7e949e]">Narrow the directory by when a contact was added.</p>
                  </div>
                  <CalendarDays size={16} className="shrink-0 text-[#20a777]" />
                </div>

                <div className="mt-4 grid grid-cols-2 rounded-xl bg-[#eef7fa] p-1 dark:bg-[#101b21]">
                  {["range", "single"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setDateMode(mode)}
                      className={`rounded-lg py-2 text-[10px] font-bold capitalize transition-all ${dateMode === mode ? "bg-white text-[#0f6392] shadow-sm dark:bg-[#24372f] dark:text-[#78c7a8]" : "text-[#5b7180]"}`}
                    >
                      {mode === "range" ? "Date range" : "Single date"}
                    </button>
                  ))}
                </div>

                <div className={`mt-4 grid gap-3 ${dateMode === "range" ? "sm:grid-cols-2" : "grid-cols-1"}`}>
                  <label className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#6a8493]">
                    {dateMode === "range" ? "From" : "Date"}
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={handleDateFrom}
                      className="mt-1.5 h-10 w-full rounded-xl border border-[#e1eaef] bg-[#f6f9fb] px-3 text-[10px] font-semibold text-[#102f45] outline-none focus:border-[#147db5] dark:border-[#354d44] dark:bg-[#121d19] dark:text-[#d7e6df] dark:[color-scheme:dark]"
                    />
                  </label>

                  {dateMode === "range" ? (
                    <label className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#6a8493]">
                      To
                      <input
                        type="date"
                        value={dateTo}
                        onChange={handleDateTo}
                        className="mt-1.5 h-10 w-full rounded-xl border border-[#e1eaef] bg-[#f6f9fb] px-3 text-[10px] font-semibold text-[#102f45] outline-none focus:border-[#147db5] dark:border-[#354d44] dark:bg-[#121d19] dark:text-[#d7e6df] dark:[color-scheme:dark]"
                      />
                    </label>
                  ) : null}
                </div>

                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={handleToday}
                    className="flex-1 rounded-xl bg-[#eaf4fa] py-2.5 text-[10px] font-bold text-[#0f6392] transition-colors hover:bg-[#dff4ea] dark:bg-[#18362d] dark:text-[#65c69f]"
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={handleClearDate}
                    className="flex-1 rounded-xl border border-[#d7e6ec] py-2.5 text-[10px] font-bold text-[#67818f] transition-colors hover:bg-[#f4f8fa] dark:border-[#304955] dark:text-[#9bb0ba]"
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="relative min-w-0">
            <button
              type="button"
              disabled={loading}
              onClick={() => toggleDropdown("sort")}
              className={`${controlClass} w-full lg:w-[155px]`}
            >
              <ArrowUpDown size={15} className="shrink-0 text-[#61756e]" />
              <span className="min-w-0 flex-1 truncate">{selectedSortLabel}</span>
              <ChevronDown
                size={14}
                className={`shrink-0 text-[#849ba7] transition-transform ${openDropdown === "sort" ? "rotate-180" : ""}`}
              />
            </button>

            {openDropdown === "sort" ? (
              <div className={`${menuClass} right-0 w-[220px]`}>
                {SORT_OPTIONS.map((option) => {
                  const active = selectedSort === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        onSortChange?.(option.value);
                        setOpenDropdown(null);
                      }}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-[11px] font-semibold transition-colors ${active ? "bg-[#eaf4fa] text-[#0f6392] dark:bg-[#1c372d] dark:text-[#79c8a9]" : "text-[#34506a] hover:bg-[#f6f9fb] dark:text-[#bacbc4] dark:hover:bg-[#22342e]"}`}
                    >
                      <span className="flex-1">{option.label}</span>
                      {active ? <Check size={13} strokeWidth={2.5} /> : null}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>

          <button
            type="button"
            disabled={loading || activeFilterCount === 0}
            onClick={handleReset}
            aria-label="Reset filters"
            className="flex h-11 items-center justify-center gap-2 rounded-xl border border-[#e1eaef] bg-[#ffffff] px-3 text-[10px] font-bold text-[#5b7180] transition-all hover:border-[#bdcbc5] hover:bg-white hover:text-[#147db5] disabled:cursor-not-allowed disabled:opacity-35 sm:px-4 dark:border-[#344b43] dark:bg-[#182521] dark:text-[#a8bdb5]"
          >
            <RotateCcw size={14} strokeWidth={2} />
            <span className="sm:hidden xl:inline">Reset</span>
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <span className="mr-1 shrink-0 text-[9px] font-bold uppercase tracking-[0.14em] text-[#7b8b85] dark:text-[#80958d]">
          View
        </span>
        {QUICK_FILTERS.map((filter) => {
          const active = selectedQuickFilter === filter.value;

          return (
            <button
              key={filter.value}
              type="button"
              disabled={loading}
              onClick={() => onQuickFilter?.(filter.value)}
              className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[10px] font-bold transition-all ${active ? "border-[#b9dff0] bg-[#eaf4fa] text-[#0f6392] shadow-[0_5px_14px_rgba(36,135,101,0.08)] dark:border-[#376451] dark:bg-[#1c372d] dark:text-[#7bc9ab]" : "border-[#e1eaef] bg-[#ffffff] text-[#5b7180] hover:border-[#bfd0c8] hover:bg-white hover:text-[#147db5] dark:border-[#344b43] dark:bg-[#182521] dark:text-[#9db3aa]"}`}
            >
              {active ? <Check size={11} strokeWidth={2.5} /> : null}
              {filter.label}
            </button>
          );
        })}

        <span className="ml-auto shrink-0 pl-3 text-[10px] font-semibold text-[#7d8d87] dark:text-[#82978f]">
          {Number(totalContacts).toLocaleString()} results
        </span>
      </div>
    </section>
  );
}

export default ContactsFilters;
